import type { RefObject } from 'react';
import { Search } from '@kyruus/search-sdk';
import { StateMachine } from 'xstate';
import { AxiosResponse } from 'axios';

import {
  MarketableLocation,
  SearchQuery,
  ExtendedProvider,
  Profile
} from 'Src/types';

export const STATES = {
  location: '@states/LOCATION',
  providers: '@states/PROVIDERS',
  initializing: '@states/INITIALIZING',
  error: '@states/ERROR',
  success: '@states/SUCCESS',
  pending: '@states/PENDING'
} as const;

export const EVENTS = {
  fetchLocationSuccess: 'done.invoke.fetchLocation',
  fetchLocationError: 'error.platform.fetchLocation',
  fetchProvidersSuccess: 'done.invoke.fetchProviders',
  fetchProvidersError: 'error.platform.fetchProviders',
  loadMoreProviders: 'FETCH_MORE_PROVIDERS',
  newProvidersSearch: 'NEW_PROVIDERS_SEARCH'
} as const;

export type LocationResult = AxiosResponse<MarketableLocation>;

export type ProvidersResult = AxiosResponse<{
  _metadata: {
    total_providers: number;
  };
  _result: Profile[];
}>;

export type NewProvidersSearch = {
  data: {
    currentQuery: SearchQuery;
  };
};

export type LocationStateMachine = StateMachine<
  LocationContext,
  LocationStateSchema,
  LocationEvent
>;

export interface LocationContext {
  currentQuery: SearchQuery;
  error?: unknown;
  fetchLocation: (props: {
    locationId: string;
    searchSDK: RefObject<Search>;
  }) => Promise<LocationResult>;
  fetchProvidersByLocation: (
    locationId: string,
    currentQuery: SearchQuery
  ) => Promise<ProvidersResult>;
  location?: MarketableLocation;
  locationId: string;
  providers?: ExtendedProvider[];
  providersError?: unknown;
  providersPage: number;
  totalProviders?: number;
  searchSDK: RefObject<Search>;
}

export interface LocationStateSchema {
  states: {
    [STATES.location]: {
      states: {
        [STATES.pending]: Record<string, unknown>;
        [STATES.success]: Record<string, unknown>;
        [STATES.error]: Record<string, unknown>;
      };
    };
    [STATES.providers]: {
      states: {
        [STATES.pending]: Record<string, unknown>;
        [STATES.success]: Record<string, unknown>;
        [STATES.error]: Record<string, unknown>;
      };
    };
  };
}

export type LocationEvent =
  | ({
      type: typeof EVENTS.fetchLocationSuccess;
    } & LocationResult)
  | { type: typeof EVENTS.fetchLocationError; error: unknown }
  | ({
      type: typeof EVENTS.fetchProvidersSuccess;
    } & ProvidersResult)
  | { type: typeof EVENTS.fetchProvidersError; error: unknown }
  | { type: typeof EVENTS.loadMoreProviders }
  | ({ type: typeof EVENTS.newProvidersSearch } & NewProvidersSearch);
